import { useState, useContext } from 'react';

import cookies from 'js-cookie';
import { useRouter } from 'next/router';

import { COOKIE_NAMES } from '../constants/cookies';
import { publicPages } from '../constants/routes';
import SidebarContext from '../context/sidebar/context';
import { ThreadContext } from '../context/thread';
import { Portal, PortalLink } from '../enums/portal.enum';
import { getFirstPathSegment } from '../utils/pathUtils';
import { SidebarLabel } from '../utils/portalMapping';
import {
  getIsResponsePopulated,
  setResponsePopulated,
} from '../utils/responsePopulatedUtil';

import { useModalState } from './useModalState';

export function useSidebarNavigation() {
  const router = useRouter();
  const [confirmationCallback, setConfirmationCallback] = useState<
    (() => void) | null
  >(null);
  const [modalRoute, setModalRoute] = useState<string | null>(null);
  const { setShowConfirmationModal, showConfirmationModal } = useModalState({});
  const { toggleSection, expandedSections } = useContext(SidebarContext);

  const threadContext = useContext(ThreadContext);

  function openModal(route: string, onConfirm?: () => void) {
    if (getIsResponsePopulated() && !showConfirmationModal) {
      setModalRoute(route);
      setShowConfirmationModal(true);
      if (onConfirm) {
        setConfirmationCallback(() => onConfirm);
      }
    }
  }

  const handleNavigate = (route: string, fromIPlanSection?: boolean) => {
    const firstSegment = getFirstPathSegment(route);
    const finalUrl =
      Object.values(PortalLink).includes(`/${firstSegment}`) ||
      publicPages.includes(route)
        ? route
        : `${PortalLink[cookies.get(COOKIE_NAMES.SELECTED_PORTAL)]}${route}`;

    if (fromIPlanSection) {
      const sectionTitle = `${SidebarLabel[Portal.PHILIPPINES]} Curriculum`;
      if (!expandedSections[sectionTitle]) {
        toggleSection(sectionTitle);
      }
    }

    if (threadContext?.isThreadedMode) {
      const component = threadContext.createPageComponent(finalUrl);
      threadContext.addPage(finalUrl, component);
      return;
    }

    if (getIsResponsePopulated()) {
      openModal(finalUrl, () => {
        router.push(finalUrl);
      });
    } else {
      router.push(finalUrl);
    }
  };

  const handleConfirm = () => {
    if (confirmationCallback) {
      confirmationCallback();
      setConfirmationCallback(null);
    }
    if (modalRoute) {
      router.push(modalRoute);
      setResponsePopulated(false);
    }
    setShowConfirmationModal(false);
  };

  return {
    handleNavigate,
    handleConfirm,
    showConfirmationModal,
    setShowConfirmationModal,
    modalRoute,
    openModal,
  };
}
